import type { ChatType } from '@form-factory/schema';
import { useLingui } from '@lingui/react/macro';
import { useFormContext } from '@utima/ui-informed';
import { useMemo } from 'react';

import { Card } from '@/components/card/Card';
import { SchemaFields } from '@/components/form/SchemaFields';
import { useFieldsSchema } from '@/components/form/useFieldsSchema';

export function ToolConfigurationFields() {
  const { t } = useLingui();
  const { initialValues } = useFormContext<ChatType>();

  // Define configurable tools - only user-configurable tools, not internal ones
  // documentSearch is always enabled, authRequired is internal and not configurable
  const tools = useMemo(
    () => [
      {
        value: 'groupClass',
        label: 'Group Class Tool',
        description: 'Retrieves group class reservations and schedules',
      },
      // Fine-grained membership actions
      {
        value: 'membership_status',
        label: 'Membership Status',
        description: 'Allow viewing membership status',
      },
      {
        value: 'user_profile',
        label: 'User Profile',
        description: 'Allow viewing user profile',
      },
      {
        value: 'membership_registration',
        label: 'Membership Registration',
        description: 'Allow starting membership registration',
      },
      {
        value: 'membership_pause',
        label: 'Pause Membership',
        description: 'Allow pausing a membership',
      },
    ],
    [],
  );

  const { schema: toolsSchema } = useFieldsSchema(
    tools.map(tool => ({
      name: `toolEnabled.${tool.value}`,
      label: t`${tool.label}`,
      type: 'checkbox',
      uiControl: 'switch',
      defaultValue: !initialValues?.disabledTools?.includes(tool.value),
      tooltip: t`Enable or disable the ${tool.label.toLowerCase()} for this chat`,
      showOptional: false,
    })),
    [t, initialValues],
  );

  return (
    <Card title={t`Agent Tools Configuration`}>
      <p className='text-sm text-gray-600 mb-4'>
        {t`Note: Document Search is always available as it provides public information.`}
      </p>
      <SchemaFields schema={toolsSchema} />
    </Card>
  );
}
